import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  type ButtonProps,
  Icon,
  InputText,
  List,
  Popover,
  Spinner,
  Stack,
  Text,
} from "@bookingcom/bui-react";
import { ArrowNavDownIcon, MagnifyingGlassIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { UserAirlineGroupEntry } from "../../types";
import { useDebounce } from "../../hooks/useDebounce";
import { codesOf, isGroupFullySelected, isGrouped, selectionOwnerIndex, toggleAirline, toggleGroup } from "./selection";
import styles from "./AirlineGroupSelector.module.css";
import { AirlineGroupSelectorAirlineListItem } from "./AirlineGroupSelectorAirlineListItem";
import { AirlineGroupSelectorGroupListItem } from "./AirlineGroupSelectorGroupListItem";

type AirlineGroupSelectorProps = {
  groups?: UserAirlineGroupEntry[];
  isLoading?: boolean;
  value?: string[];
  label?: string;
  placeholder?: string;
  hideSubmitButton?: boolean;
  isAirlineUser?: boolean;
  onChange?: (codes: string[]) => void;
  onSubmit?: (codes: string[]) => void;
};

const matchesQuery = (query: string, entry: UserAirlineGroupEntry): UserAirlineGroupEntry | null => {
  if (!query) return entry;
  const lower = query.toLowerCase();
  if (entry.group?.name.toLowerCase().includes(lower)) return entry;
  const airlines = entry.airlines.filter(
    (airline) => airline.name.toLowerCase().includes(lower) || airline.code.toLowerCase().includes(lower)
  );
  return airlines.length > 0 ? { ...entry, airlines } : null;
};

const describeSelection = (groups: UserAirlineGroupEntry[], selected: string[]): string | null => {
  if (selected.length === 0) return null;

  const ownerIndex = selectionOwnerIndex(groups, selected);
  if (ownerIndex === -1) return null;

  const entry = groups[ownerIndex];
  if (isGrouped(entry)) {
    const name = entry.group?.name ?? "";
    return `${name} (${selected.join(", ")})`;
  }
  return entry.airlines.find((airline) => airline.code === selected[0])?.name ?? null;
};

export const AirlineGroupSelector = ({
  groups = [],
  isLoading,
  value = [],
  label,
  placeholder,
  hideSubmitButton,
  isAirlineUser,
  onChange,
  onSubmit,
}: AirlineGroupSelectorProps) => {
  const i18n = useI18n();
  const [active, setActive] = useState(false);
  const [input, setInput] = useState("");
  const [selectedCodes, setSelectedCodes] = useState(value);
  const query = useDebounce(input, 300);

  useEffect(() => {
    setSelectedCodes(value);
  }, [value]);

  const resolvedLabel = label ?? i18n.trans(t("flights_airline_portal_group_view_selector_label"));
  const resolvedPlaceholder =
    placeholder ?? i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_placeholder"));

  const filteredGroups = useMemo(
    () =>
      groups
        .map((entry, index) => ({ entry: matchesQuery(query, entry), index }))
        .filter((item): item is { entry: UserAirlineGroupEntry; index: number } => !!item.entry),
    [groups, query]
  );

  const triggerText = useMemo(() => describeSelection(groups, selectedCodes), [groups, selectedCodes]);

  const update = useCallback(
    (codes: string[]) => {
      setSelectedCodes(codes);
      onChange?.(codes);
    },
    [onChange]
  );

  const handleSubmit = () => onSubmit?.(selectedCodes);

  const handleClose = () => {
    setActive(false);
    setInput("");
  };

  const isDisabled = isLoading || groups.length === 0;

  return (
    <Stack direction="row" alignItems="end" gap={2}>
      <Stack gap={1}>
        <Text variant="emphasized_2">{resolvedLabel}</Text>
        <Popover
          closeAriaLabel="Close dropdown"
          position="bottom-start"
          forcePosition={true}
          hideArrow={true}
          hideClose={true}
          keepMounted={false}
          size="medium"
          fill
          active={active}
          onOpen={() => setActive(true)}
          onClose={handleClose}
        >
          <Popover.Trigger>
            {(attributes: ButtonProps["attributes"]) => (
              <Button
                size="large"
                variant="secondary-neutral"
                icon={isLoading ? <Spinner size="small" /> : ArrowNavDownIcon}
                iconPosition="end"
                ariaLabel="Select airlines"
                className={styles.trigger}
                attributes={attributes}
                disabled={isDisabled}
              >
                {triggerText ? (
                  <Text variant="emphasized_2">{triggerText}</Text>
                ) : (
                  <Text variant="emphasized_2" color="disabled">
                    {resolvedPlaceholder}
                  </Text>
                )}
              </Button>
            )}
          </Popover.Trigger>
          <Popover.Content>
            <Stack gap={0} mixin={{ paddingBlockEnd: 4 }}>
              <Stack mixin={{ paddingBlockStart: 4, paddingBlockEnd: 1, paddingInlineStart: 4, paddingInlineEnd: 4 }}>
                <InputText
                  className={styles.searchInput}
                  name="airline-group-selector-search"
                  placeholder={i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_placeholder"))}
                  startIcon={<Icon svg={MagnifyingGlassIcon} />}
                  value={input}
                  onChange={({ value: next }) => setInput(next)}
                />
              </Stack>
              {filteredGroups.length === 0 ? (
                <Stack mixin={{ paddingBlockStart: 3, paddingBlockEnd: 3, paddingInlineStart: 4, paddingInlineEnd: 4 }}>
                  <Text variant="body_2" color="neutral_alt">
                    {i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_search_error"))}
                  </Text>
                </Stack>
              ) : (
                <List
                  className={styles.list}
                  aria-label={`${resolvedLabel} options`}
                  attributes={{ "data-testid": "AirlineGroupSelectorList" }}
                >
                  {filteredGroups.map(({ entry, index }) => {
                    const unfilteredGroup = groups.find(({ group }) => group?.name && group.name === entry.group?.name);
                    const isGroupAirlinesSelected =
                      unfilteredGroup && codesOf(unfilteredGroup).some((code) => selectedCodes.includes(code));
                    const isGroupSelected = !!unfilteredGroup && isGroupFullySelected(unfilteredGroup, selectedCodes);

                    return (
                      <Fragment key={index}>
                        {unfilteredGroup && (
                          <AirlineGroupSelectorGroupListItem
                            entry={entry}
                            isGroupSelected={isGroupSelected}
                            isGroupAirlinesSelected={isGroupAirlinesSelected}
                            disabled={isAirlineUser && isGroupSelected}
                            onChange={() => update(toggleGroup(groups, selectedCodes, index))}
                          />
                        )}
                        {(!unfilteredGroup || isGroupAirlinesSelected || !!query) &&
                          entry.airlines.map((airline) => {
                            const isSelected = selectedCodes.includes(airline.code);
                            const isLastSelected = isSelected && selectedCodes.length === 1;

                            return (
                              <AirlineGroupSelectorAirlineListItem
                                key={airline.code}
                                airline={airline}
                                isGrouped={!!unfilteredGroup}
                                isSelected={isSelected}
                                disabled={isAirlineUser && isLastSelected}
                                onChange={() => update(toggleAirline(groups, selectedCodes, airline.code))}
                              />
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </List>
              )}
            </Stack>
          </Popover.Content>
        </Popover>
      </Stack>
      {!hideSubmitButton && (
        <Button variant="primary" size="large" onClick={handleSubmit} disabled={isDisabled}>
          {i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_button"))}
        </Button>
      )}
    </Stack>
  );
};
